import { styled, Stack, Divider } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import React from 'react';
import OptimizationHeader from './OptimizationHeader';
import OptimizationContext from './OptimizationContext';
import { motion } from 'framer-motion';
import OptimizationActions from './OptimizationActions';
import { useAcceptRecommendationMutation, useDeclineRecommendationMutation, useUndoCustomRecommendationMutation, useUndoRecommendationMutation } from '../../../../api/itemRecommendationSlice';
import LogOnEnter from '../../../CustomComponents/LogOnEnter';
import OptimizationBody from './OptimizationBody';
import { useLogUserActionMutation } from '../../../../api/physicianDashboardSlice';

function Optimization({ propItem, recommendation, draft, openItemView, hideContext = false, hideDetailsButton = false }) {
    const theme = useTheme();
    const [acceptRecommendation] = useAcceptRecommendationMutation();
    const [undoRecommendation] = useUndoRecommendationMutation();
    const [declineRecommendation] = useDeclineRecommendationMutation();
    const [undoCustomRecommendation] = useUndoCustomRecommendationMutation();
    const [logUserAction] = useLogUserActionMutation();

    const item = (recommendation.type === 'addition' || recommendation.status === 'custom_addition') ? 
        recommendation.item : 
        propItem;

    function handleEdit() {
        if (recommendation.type === 'addition' || recommendation.status === 'custom_addition') {
            openItemView(recommendation.item);
        } else {
            openItemView(item);
        }
    }

    const logRecommendationAction = async(action) => {
        await logUserAction({
            action: action,
            data: {
                location: 'Optimization',
                item: propItem,
                recommendation: recommendation
            }
        });
    }

    const handleUndo = async(event) => {
        event.stopPropagation();
        if (recommendation.type === 'custom_user_edit') {
            await undoCustomRecommendation({
                draft: draft,
                recommendation: recommendation,
            });
        } else {
            await undoRecommendation({
                draft: draft,
                recommendation: recommendation,
            });
        }

        logRecommendationAction('undid_recommendation_change');
    }

    const handleAccept = async(event) => {
        event.stopPropagation();
        await acceptRecommendation({
            draft: draft,
            recommendation: recommendation,
        });

        logRecommendationAction('accepted_recommendation');
    }

    const handleDecline = async(event) => {
        event.stopPropagation();
        await declineRecommendation({
            draft: draft,
            recommendation: recommendation,
        }); 

        logRecommendationAction('declined_recommendation');
    }

    const variants = {
        open: { 
            backgroundColor: `${theme.palette.background.white}`,
            border: '',
            transition: {
                duration: 0.3,
            }
        },
        accepted: { 
            transition: {
                duration: 0.3,
            },
        },
        rejected: { 
            transition: {
                duration: 0.3,
            }
        },
        custom_removal: { 
            transition: {
                duration: 0.3,
            }
        },
        custom_update: { 
            transition: {
                duration: 0.3,
            }
        },
        custom_swap: {
            transition: {
                duration: 0.3,
            }
        }
    };

    const context = {
        open: {
            opacity: 1,
            height: 'auto',
            marginTop: '8px',
            transition: {
                duration: 0.3,
                ease: 'easeInOut'
            }
        },
        accepted: {
            opacity: 0,
            height: 0,
            marginTop: 0,
            transition: {
                duration: 0.3,
                ease: 'easeInOut'
            },
        },
        rejected: {
            opacity: 0,
            height: 0,
            marginTop: 0,
            transition: {
                duration: 0.3,
                ease: 'easeInOut'
            }
        },
        custom_removal: {
            opacity: 0,
            height: 0,
            marginTop: 0,
            transition: {
                duration: 0.3,
                ease: 'easeInOut'
            }
        },
        custom_update: {
            opacity: 0,
            height: 0,
            marginTop: 0,
            transition: {
                duration: 0.3,
                ease: 'easeInOut'
            }
        },
        custom_swap: {
            opacity: 0,
            height: 0,
            marginTop: 0,
            transition: {
                duration: 0.3,
                ease: 'easeInOut'
            }
        },
        custom_addition: {
            opacity: 0,
            height: 0,
            marginTop: 0,
            transition: {
                duration: 0.3,
                ease: 'easeInOut'
            }
        }
    };

    return (
        <LogOnEnter action='viewed_recommendation' data={{ recommendation }}>
            <OptimizationBox
                component={motion.div} initial={false} animate={recommendation.status} variants={variants} transition={{ duration: 1.2, type: 'spring', bounce: 0.1 }}
            >
                <Stack>
                    <Stack px={2} pt={1.5} pb={0.5} direction='row' alignItems='center'>
                        <Stack spacing={1} width='100%'>
                            <OptimizationHeader recommendation={recommendation} item={item} />
                            <OptimizationBody 
                                recommendation={recommendation} 
                                item={item} 
                                yearlyUsage={draft.card.yearly_stats?.usage_count} 
                            />
                        </Stack>
                    </Stack>
                    { !hideContext && 
                        <Stack component={motion.div} initial={false} variants={context} animate={recommendation.status}>
                            <Divider />
                            <Stack px={2}>
                                <OptimizationContext item={item} recommendation={recommendation} />
                            </Stack>
                        </Stack>
                    }
                    <Divider />
                    <OptimizationActions 
                        handleEdit={handleEdit} 
                        handleAccept={handleAccept} 
                        handleDecline={handleDecline} 
                        handleUndo={handleUndo} 
                        status={recommendation.status}
                        type={recommendation.type}
                        hideDetailsButton={hideDetailsButton}
                        locked={recommendation.clinical_review && recommendation.clinical_review?.submitted_at !== null}
                    />
                </Stack>
            </OptimizationBox>
        </LogOnEnter>
    )
}

const OptimizationBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.white,
    borderRadius: '4px',
}));

export default Optimization;